import { useEffect, useEffectEvent, useMemo, useRef } from "react";
import { usePage } from "@inertiajs/react";
import { useAdsChatSubmit } from "@components/ads/hooks";
import type { CampaignProps } from "@components/ads/workflow-panel/workflow-buddy/ad-campaign.types";
import { Workflow, type UUIDType, Ads } from "@shared";

export function useStageInit(stage: Workflow.AdsSubstepName) {
  const props = usePage<CampaignProps>().props;
  const { project, campaign } = props;
  const submit = useAdsChatSubmit();

  const assets = useMemo(
    () => ({
      headlines: props.headlines?.length ? props.headlines : undefined,
      descriptions: props.descriptions?.length ? props.descriptions : undefined,
      callouts: props.callouts?.length ? props.callouts : undefined,
      structuredSnippets: props.structured_snippet || undefined,
      keywords: props.keywords?.length ? props.keywords : undefined,
    }),
    [props.headlines, props.descriptions, props.callouts, props.structured_snippet, props.keywords]
  );

  const isGenerating = useRef(false);
  const attemptCount = useRef(0);

  // Reset attempt counter when navigating to a different stage
  useEffect(() => {
    attemptCount.current = 0;
  }, [stage]);

  const maybeInitializeStage = useEffectEvent(() => {
    if (isGenerating.current) return;
    if (Ads.stageLoadedSuccessfully(assets as any, stage)) return;
    if (!project?.uuid) return;
    if (!submit) return;
    // Prevent infinite retry loop
    if (attemptCount.current >= 1) return;
    isGenerating.current = true;
    attemptCount.current += 1;

    submit({
      intent: {
        type: "switch_page" as const,
        payload: { stage },
        createdAt: new Date().toISOString(),
      },
      stage,
      projectUUID: project.uuid as UUIDType,
      campaignId: campaign?.id,
    })?.finally(() => {
      isGenerating.current = false;
    });
  });

  useEffect(() => {
    maybeInitializeStage();
  }, [stage, project?.uuid, campaign?.id, assets]);
}
